import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { StateService, TransitionService, StateRegistry } from '@uirouter/angular';
import { UIRouterGlobals } from '@uirouter/core';
import { AnalyticsRouterConfigService } from './hooks/analytics/analytics-router-config.service';

import type { HookMatchCriteria, RawParams, StateOrName, StateParams, Transition, TransitionPromise } from '@uirouter/angular';
import type { RouterBaseService, LinkParameters, StateDeclaration, StateObject, TransitionOptions, DataAnalyticsEvent, DataAnalyticsInitialData } from '@tdwb/core';

@Injectable()
export class RouterService implements RouterBaseService {
  constructor(
    @Inject(StateService) private stateService: StateService,
    @Inject(TransitionService) private transitionService: TransitionService,
    @Inject(UIRouterGlobals) private uiRouter: UIRouterGlobals,
    @Inject(StateRegistry) private stateRegistry: StateRegistry,
    @Inject(AnalyticsRouterConfigService) private analyticsRouterConfigService: AnalyticsRouterConfigService
  ) { }

  getStateUrl(stateName: string, params: RawParams): string {
    return this.stateService.href(stateName, params);
  }

  getCurrentStateName(): string {
    return this.stateService.$current.name;
  }

  getCurrentStateParams(): LinkParameters {
    return this.stateService.$current.params;
  }

  getGlobalsCurrent(): StateDeclaration {
    return this.uiRouter.current as StateDeclaration;
  }

  getStateRegistry(value: string): StateDeclaration {
    return this.stateRegistry.get(value) as StateDeclaration;
  }

  getStateHref(name: string): string {
    return this.stateService.href(name);
  }

  getStateParams(decode: boolean = true): RawParams {
    return this.parseParams(this.stateService.params, decode);
  }

  // Deprecated
  getStateParamsAll(): StateParams {
    return this.stateService.params
  }

  go(to: unknown, params?: unknown, options?: unknown): TransitionPromise {
    return this.stateService.go(to, params, options);
  }

  transitionTo(to: unknown, params?: unknown, options?: unknown): void {
    this.stateService.transitionTo(to, params, options);
  }

  includes(stateName: unknown, stateParams?: unknown, options?: unknown): boolean {
    return this.stateService.includes(stateName, stateParams, options);
  }

  getParamsValue(key: string): unknown {
    return this.uiRouter.params?.[key];
  }

  getRouteData(): unknown {
    return this.stateService.current.data;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  transitionOnStart(fun: (() => void)): Function {
    return this.transitionService.onStart({ to: '**' }, (): void => {
      fun();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  transitionOnSuccess(criteria: unknown, fun: ((options: TransitionOptions) => void)): Function {
    return this.transitionService.onSuccess(criteria as HookMatchCriteria, (transition: Transition): void => {
      fun(transition.options());
    });
  }
  /* eslint-enabled @typescript-eslint/ban-types */

  setPageTitle(fun: ((transitionTo: StateObject) => void)): void {
    this.transitionService.onSuccess({}, (transition: Transition): void => {
      fun(transition.$to());
    });
  }

  setChatInit(fun: ((transitionTo: StateDeclaration) => void)): void {
    this.transitionService.onSuccess({ to: '**' }, (transition: Transition): void => {
      fun(transition.to() as StateDeclaration);
    }, { priority: 1 });
  }

  setGlobalNotificationBanner(fun: ((to: StateDeclaration) => void)): void {
    this.transitionService.onSuccess({ to: 'page.**' }, (transition: Transition): void => {
      fun(transition.to() as StateDeclaration);
    }, { priority: 1 });
  }

  setDataAnalyticsHooks(initData: DataAnalyticsInitialData): Observable<DataAnalyticsEvent> {
    return this.analyticsRouterConfigService.initHooks(initData);
  }

  reload(reloadState?: StateOrName): Promise<StateObject> {
    return this.stateService.reload(reloadState);
  }

  private parseParams(linkParams: LinkParameters, decode: boolean): RawParams {
    const urlParams: RawParams = {};
    if (linkParams !== undefined) {
      Object.keys(linkParams).forEach((x: string): void => {
        const params = linkParams[x];
        if (params !== null && params !== undefined) {
          urlParams[x] = decode ? decodeURIComponent(params as string) : params;
        } else {
          urlParams[x] = undefined;
        }
      });
    }
    return urlParams;
  }
}
